import dayjs, { ConfigType } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export const getRelativeTime = (dateStr?: string): string => {
  if (!dateStr) return '';

  return dayjs(dateStr).fromNow();
};

export const formatHistoryTableDate = (dateStr: string): string => {
  const date = dayjs(dateStr);
  const today = dayjs();

  if (date.isSame(today, 'day')) {
    return `Today ${date.format('h:mm A')}`;
  } else if (date.isSame(today.subtract(1, 'day'), 'day')) {
    return `Yesterday ${date.format('h:mm A')}`;
  } else {
    return date.format('MMM D, h:mm A');
  }
};

/**
 * @deprecated - Please use `getFullName` from `@frontend/name` instead.
 */
export const getFullName = <
  T extends Partial<Record<'firstName' | 'lastName' | 'FirstName' | 'LastName', string>> | undefined | null
>(
  entity: T
) => {
  if (!entity) return '';
  return `${entity.firstName || entity.FirstName || ''} ${entity.lastName || entity.LastName || ''}`.trim();
};

export const isValidDate = (date: dayjs.Dayjs): boolean => {
  // Check if the date is valid and not before the year 1900
  return date.isValid() && date.year() >= 1900;
};

// Define an interface for the input options
export interface FormatDateTimeOptions {
  fromTimezone?: string; // The source timezone (e.g., 'America/New_York')
  toTimezone?: string; // The target timezone (e.g., 'Asia/Tokyo')
  inputFormat?: string; // The format of the input date-time (optional)
  outputFormat?: string; // The desired output format (optional)
}

// TODO: Move this to a common place to be used for both Schedule and Quick Fill
export function formatDateTime(inputDateTime: ConfigType, options?: FormatDateTimeOptions): string {
  const { fromTimezone, toTimezone, inputFormat, outputFormat = 'MM/DD/YYYY hh:mm A' } = options || {};

  if (!inputDateTime) {
    return '';
  }

  // remove Z from the input date-time string to avoid parsing issues (as for that case, it's already in UTC)
  if (typeof inputDateTime === 'string' && inputDateTime.endsWith('Z')) {
    inputDateTime = inputDateTime.slice(0, -1);
  }

  const date = inputFormat ? dayjs(inputDateTime, inputFormat) : dayjs(inputDateTime);

  if (!isValidDate(date)) {
    return '';
  }

  // Assuming we no need to convert to timezone if either of from or to timezone is not provided
  if (!fromTimezone || !toTimezone) {
    return date.format(outputFormat);
  }

  const convertedDate = date.tz(fromTimezone, true).tz(toTimezone);

  return convertedDate.format(outputFormat);
}

// TODO: Move this to a common place to be used for both Schedule and Quick Fill
// Returns input label with timezone abbreviation
export const getLabelWithTimezoneAbbr = (label: string, timezoneAbbr: string) => {
  if (timezoneAbbr) {
    return `${label} ${timezoneAbbr ? `(${timezoneAbbr})` : ''}`.trim();
  }
  return label;
};
